import { ConfigProvider } from 'antd';
import React, { useMemo, useState } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { getAntdThemeConfig } from 'utils/color/antdTheme';
import { type HorizonTheme, Theme, Themes, ThemeStorageKey } from 'utils/color/horizonTheme';
import { getItem as getStorageItem, setItem as setStorageItem } from 'utils/storage';

const prefersReducedMotion = () =>
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const ThemeProvider: React.FC<{ theme: Omit<HorizonTheme, 'onChange'> }> = ({
  children,
  ...props
}) => {
  const [currentThemeName, setCurrentThemeName] = useState<Theme>(
    (getStorageItem(ThemeStorageKey) ?? Theme.Light) as Theme
  );

  // Read once: antd wraps its children in a motion provider the first time the motion token
  // differs from its default, which remounts them, so the token stays the same for the session
  // and a change to the OS setting applies after a reload.
  const [reducedMotion] = useState(prefersReducedMotion);

  // Memoized: a new theme object is a context change for every antd component in the app.
  const antdTheme = useMemo(() => {
    const config = getAntdThemeConfig(Themes[currentThemeName]);
    // antd animates popups, modals and panels even when the OS asks for reduced motion.
    return reducedMotion ? { ...config, token: { ...config.token, motion: false } } : config;
  }, [currentThemeName, reducedMotion]);

  const handleChange = (themeName: Theme) => {
    setCurrentThemeName(themeName);
    setStorageItem(ThemeStorageKey, themeName);
  };

  return (
    <StyledComponentsThemeProvider
      {...props}
      theme={{
        ...Themes[currentThemeName],
        onChange: handleChange,
      }}
    >
      <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>
    </StyledComponentsThemeProvider>
  );
};
